import React, { useEffect, useState, useCallback } from "react";
import { Pagination } from "antd";
import LoaderOverlay from "../../components/Loader";
import axios from "axios";
import { useSelector } from "react-redux";
import { baseUrl } from "../../utils/api/apis";
import { useNavigate } from "react-router-dom";

const UserNotification = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const idToken = useSelector((state) => state.auth.idToken);
  const { selectedBrand } = useSelector((state) => state.brand);
  const navigate = useNavigate();

  const fetchMessages = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
          params: {
            page: page,
            page_size: 5,
          },
        };

        const response = await axios.get(
          `${baseUrl}auth/notifications/admin/sent/`,
          config,
        );
        const data = response.data;

        if (data && data.results) {
          setMessages(data.results);
          setTotalCount(data.count);
        } else {
          // Fallback in case not paginated format
          setMessages(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [idToken],
  );

  useEffect(() => {
    if (idToken) {
      fetchMessages(currentPage);
    }
  }, [idToken, currentPage, fetchMessages]);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (messages.length > 0) {
      handleSelectMessage(messages[0]);
    } else {
      setSelectedId(null);
    }
  }, [messages]);

  const handleSelectMessage = (msg) => {
    setSelectedId(msg.id || msg.notification_uid);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const selectedMessagePreview = messages.find(
    (m) => (m.id || m.notification_uid) === selectedId,
  );

  return (
    <div className="inbox-container">
      <button
        className={`send-notification-btn ${selectedBrand?.brand === "crypto_edge" ? "crypto-brand" : ""}`}
        onClick={() => navigate("/notification/send")}
      >
        Send Notification
      </button>
      <div className="inbox-wrapper">
        <div className="inbox-sidebar">
          <h3 className="sidebar-header">All Messages</h3>

          <div className="message-list">
            {isLoading ? (
              <LoaderOverlay />
            ) : (
              <>
                {messages.map((msg) => {
                  const msgId = msg.id || msg.notification_uid;
                  return (
                    <div
                      key={msgId}
                      className={`message-item ${selectedId === msgId ? "active" : ""}`}
                      onClick={() => handleSelectMessage(msg)}
                    >
                      <div className="msg-header-row">
                        <span className="msg-title">{msg.subject}</span>
                      </div>
                      <p className="msg-preview">
                        {msg.body?.replace(/<[^>]+>/g, "").substring(0, 50)}...
                      </p>
                      <span className="msg-date">{formatDate(msg.created_at)}</span>
                    </div>
                  );
                })}

                {messages.length === 0 && (
                  <div className="empty-state">No messages found.</div>
                )}
              </>
            )}
          </div>

          {totalCount > 5 && (
            <div className="pagination-wrapper">
              <Pagination
                current={currentPage}
                total={totalCount}
                pageSize={5}
                onChange={onPageChange}
                size="small"
              />
            </div>
          )}
        </div>

        <div className="inbox-content">
          {selectedId && selectedMessagePreview ? (
            <div className="message-detail">
              <h2 className="detail-title">{selectedMessagePreview.subject}</h2>
              <span className="detail-date">
                {formatDate(selectedMessagePreview.created_at)}
              </span>

              <div className="detail-body">
                {selectedMessagePreview.content_type === "html" ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: selectedMessagePreview.body,
                    }}
                  />
                ) : (
                  <>
                    {(selectedMessagePreview.body || "")
                      .toString()
                      .split("\n")
                      .map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="empty-state">Select a message to view details</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotification;
